import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Tag, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BusinessCard, ListingCard, ServiceCard, RequestCard } from "@/components/cards";
import { EmptyState, Spinner } from "@/components/common";

const TABS = [
  { id: "businesses", label: "Businesses", path: "/businesses", Card: BusinessCard, catKey: "business_categories" },
  { id: "marketplace", label: "Marketplace", path: "/marketplace", Card: ListingCard, catKey: "marketplace_categories" },
  { id: "services", label: "Services", path: "/services", Card: ServiceCard, catKey: "service_categories" },
  { id: "requests", label: "Requests", path: "/requests", Card: RequestCard, catKey: "service_categories" },
];

export default function Explore({ initialTab }) {
  const [params, setParams] = useSearchParams();
  const { config } = useAuth();
  const [tab, setTab] = useState(initialTab || params.get("tab") || "businesses");
  const [q, setQ] = useState(params.get("q") || "");
  const [state, setState] = useState(params.get("state") || "all");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [sort, setSort] = useState("newest");
  const [items, setItems] = useState(null);

  const current = TABS.find((t) => t.id === tab);
  const cats = config?.[current.catKey] || [];
  const states = config?.states || [];

  const load = useCallback(() => {
    setItems(null);
    const p = { limit: 40 };
    if (q) p.q = q;
    if (state !== "all") p.state = state;
    if (category !== "all") p.category = category;
    if (tab === "marketplace") p.sort = sort;
    api.get(current.path, { params: p }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, [tab, q, state, category, sort, current.path]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => { e?.preventDefault(); setParams({ tab, q, ...(state !== "all" ? { state } : {}), ...(category !== "all" ? { category } : {}) }); load(); };

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl font-bold tracking-tight">Explore Mtaa</h1>
      <form onSubmit={onSearch} className="flex flex-col gap-2 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="border-0 shadow-none focus-visible:ring-0" data-testid="explore-search" />
        </div>
        <Button type="submit" data-testid="explore-search-btn">Search</Button>
      </form>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setCategory("all"); }}>
        <TabsList className="w-full justify-start overflow-x-auto no-scrollbar">
          {TABS.map((t) => <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`}>{t.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <Link to="/deals" data-testid="deals-promo-strip"
        className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10"><Tag className="h-5 w-5 text-primary" /></div>
          <div>
            <p className="font-display text-[15px] font-semibold">Mtaa Deals</p>
            <p className="text-xs text-muted-foreground">Deals and useful finds from around the web.</p>
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-primary">Explore Deals <ArrowRight className="h-4 w-4" /></span>
      </Link>

      <div className="flex flex-wrap gap-2">
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="w-32" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All states</SelectItem>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        {tab === "marketplace" && (
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-36" data-testid="filter-sort"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="newest">Newest</SelectItem><SelectItem value="price_low">Price: low</SelectItem><SelectItem value="price_high">Price: high</SelectItem></SelectContent>
          </Select>
        )}
      </div>

      {items === null ? <div className="flex justify-center py-16"><Spinner className="h-7 w-7" /></div> :
        items.length === 0 ? <EmptyState title="No results found" subtitle="Try a different search, category, or search Australia-wide." /> : (
          <div className={tab === "businesses" || tab === "marketplace"
            ? "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
            : "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"}>
            {items.map((it) => { const C = current.Card; return <C key={it.id} b={it} l={it} s={it} r={it} />; })}
          </div>
        )}
    </div>
  );
}
